import {Icon} from '@/app/components/Icon';
import {cn} from '@/lib/utils';
import {useEffect, useRef, useState} from 'react';
import {NavLink, useNavigate, useSearchParams} from 'react-router-dom';
import {Button} from '@/components/app/button';
import {Modal} from '@/components/app/modal';
import {InputGroup} from '@/components/app/input-group';
import {InputAdornment} from '@/components/app/input-group';
import {Form} from '../../../app/components/Form';
import {
  useRegisterMutation,
  useRegisterResendOTPMutation,
  useRegisterVerifyOTPMutation,
} from '../../../app/services/identitiesApi';
import {emailRegExp} from '../../../app/utils/miscUtils';
import {AuthLayout} from '../components/AuthLayout';
import {OTPForm} from '../components/OTPForm';
import {TermsOfUse} from '../components/TermsOfUse';
import {
  containsNumericMessage,
  containsNumericRegex,
  containsSpecialCharacterMessage,
  containsSpecialCharacterRegex,
  minLengthMessage,
  minLengthRegex,
  validatePassword,
} from '../validatePassword';
import {CheckList} from '../../../app/components/CheckList';
enum Step {
  Username = 'username',
  OTP = 'otp',
}
interface FieldValues {
  confirmPassword: string;
  email: string;
  name: string;
  newPassword: string;
}
export const Register = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [register] = useRegisterMutation();
  const [registerResendOTP] = useRegisterResendOTPMutation();
  const [registerVerifyOTP] = useRegisterVerifyOTPMutation();
  const [username, setUsername] = useState('');
  const [step, setStep] = useState(() =>
    username === '' ? Step.Username : ((searchParams.get('step') as Step | null) ?? Step.Username),
  );
  const [newPassword, setNewPassword] = useState('');
  const [newPasswordFocused, setNewPasswordFocused] = useState(false);
  const [termsOfUseVisible, setTermsOfUseVisible] = useState(false);
  const initialSearchParams = useRef(searchParams);
  useEffect(() => {
    setSearchParams({
      ...Object.fromEntries(initialSearchParams.current.entries()),
      step,
    });
    setNewPassword('');
  }, [step]);
  return (
    <AuthLayout
      renderContent={step === Step.OTP ? renderFormOTP : renderFormEmail}
      subtitle={
        step === Step.OTP ? (
          <>
            Please enter the 6-digit confirmation code sent to <span className="font-bold">{username}</span>.
          </>
        ) : (
          'Create your Biotandem account to get started.'
        )
      }
      title={step === Step.OTP ? 'Enter Confirmation Code' : 'Sign Up'}
    />
  );
  function renderFormEmail() {
    return (
      <>
        <Form<FieldValues>
          fields={[
            {
              name: 'email',
              rules: {
                maxLength: {
                  value: 100,
                  message: 'Email cannot exceed 100 characters.',
                },
                pattern: {
                  value: emailRegExp,
                  message: 'Invalid email address. E.g. example@email.com',
                },
                required: 'Email is required.',
              },
              type: 'email',
              typeOptions: {
                autoFocus: true,
                className: 'h-[3rem]',
                placeholder: 'Enter a valid email address',
              },
            },
            {
              name: 'newPassword',
              rules: {
                required: 'Password is required.',
                validate: (value) => validatePassword(value as string),
              },
              type: 'password',
              typeOptions: {
                autoComplete: 'new-password',
                feedback: false,
                id: 'password',
                inputClassName: 'h-[3rem]',
                onFocus: () => setNewPasswordFocused(true),
                onBlur: () => setNewPasswordFocused(false),
                placeholder: 'Choose a secure password',
                toggleMask: true,
              },
            },
          ]}
          onChange={(fieldValues) => {
            setNewPassword(fieldValues.newPassword ?? '');
          }}
          onSubmit={(fieldValues) =>
            register({
              ...fieldValues,
              confirmPassword: fieldValues.newPassword,
              name: fieldValues.email.split('@')[0].slice(0, 50),
              username: fieldValues.email,
            })
              .unwrap()
              .then(() => {
                setUsername(fieldValues.email);
                setNewPasswordFocused(false);
                setStep(Step.OTP);
              })
          }
          render={({renderError, renderFieldByName, renderSubmitButton}) => (
            <div className={'flex flex-col gap-4 w-full'}>
              {renderError()}
              <InputGroup position="left" className="[--input-group-height:3rem]">
                <Icon icon="envelope" />
                {renderFieldByName('email')}
              </InputGroup>
              <div className="relative">
                {newPasswordFocused && (
                  <div
                    className="absolute bottom-full left-0 right-0 p-4 border border-gray-200 rounded-3xl shadow-md mb-2"
                    style={{
                      backgroundColor: 'rgba(255, 255, 255, 1)',
                    }}
                  >
                    <div className="mb-2 font-bold">Password must have</div>
                    <CheckList
                      items={[
                        {
                          checked: newPassword.match(minLengthRegex) != null,
                          label: minLengthMessage,
                        },
                        {
                          checked: newPassword.match(containsNumericRegex) != null,
                          label: containsNumericMessage,
                        },
                        {
                          checked: newPassword.match(containsSpecialCharacterRegex) != null,
                          label: containsSpecialCharacterMessage,
                        },
                      ]}
                    />
                  </div>
                )}
                <InputGroup position="left" className="[--input-group-height:3rem]">
                  <div>{renderFieldByName('newPassword')}</div>
                  <Icon icon="lock" />
                </InputGroup>
              </div>
              <div className={'mb-4 leading-none text-sm pl-4 text-muted-foreground'}>
                By clicking Create an account, you agree to our{' '}
                <Button
                  className={'leading-none p-0'}
                  onClick={() => setTermsOfUseVisible(true)}
                  size="sm"
                  type={'button'}
                  variant="link"
                >
                  {'Terms of Use'}
                </Button>
                .
              </div>
              {renderSubmitButton()}
              <div className={'mt-4'}>
                <hr className={'border-gray-500'} />
                <div className={'text-center mt-4'}>
                  <span className={'text-muted-foreground'}>Already have an account?</span>{' '}
                  <NavLink to={'/sign-in'}>Sign In</NavLink>
                </div>
              </div>
            </div>
          )}
          submitButton={{
            className: 'h-[3rem]',
            children: 'Create an account',
          }}
        />
        <Modal
          contentClassName={'pb-0'}
          dismissOnOutsideClick
          footer={
            <Button
              className={cn('text-muted-foreground', 'rounded-full')}
              onClick={() => setTermsOfUseVisible(false)}
              type={'button'}
              variant="ghost"
            >
              {'Close'}
            </Button>
          }
          onClose={() => setTermsOfUseVisible(false)}
          showHeader={false}
          style={{
            height: '100%',
            width: 'min(1024px, calc(100vw - 2rem))',
          }}
          open={termsOfUseVisible}
        >
          <TermsOfUse />
        </Modal>
      </>
    );
  }
  function renderFormOTP() {
    return (
      <OTPForm
        cancel={() => setStep(Step.Username)}
        cancelText="Change Email"
        resend={() =>
          registerResendOTP({
            usernameOrEmail: username,
          }).unwrap()
        }
        verify={(otp) =>
          registerVerifyOTP({
            usernameOrEmail: username,
            otp,
          })
            .unwrap()
            .then(() => navigate(`/welcome?${initialSearchParams.current}`))
        }
      />
    );
  }
};
